/**
 * Rôle de cet appareil (réglé par le gérant dans Configuration › Appareils).
 * Gardé sur l'appareil pour démarrer tout de suite sur le bon écran, relu chaque minute.
 */
import { useEffect } from 'react';
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { getMyDeviceRole, parseDeviceRole, type DeviceRole } from '@/api/devices/deviceRoleApi';
import { useAuth } from '@/contexts/AuthContext';
import { getOrCreateDeviceId } from '@/hooks/useDeviceHeartbeat';

const STORAGE_KEY = 'vp_device_role';
const REFRESH_MS = 60_000;

type StoredRole = { tenantId: string; role: DeviceRole };

/**
 * Dernier rôle connu de l'appareil. Commerce pas encore chargé (démarrage, session
 * rafraîchie) : on garde ce rôle plutôt que de basculer un instant sur l'appli complète.
 */
const readStored = (tenantId: string | null): DeviceRole | null => {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const stored = JSON.parse(raw) as Partial<StoredRole>;
    if (!tenantId || stored.tenantId === tenantId) return parseDeviceRole(stored.role);
    return null;
  } catch {
    return null;
  }
};

const writeStored = (value: StoredRole): void => {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(value));
  } catch {
    // Stockage bloqué : le rôle sera relu au prochain démarrage.
  }
};

export function useDeviceRole(): { role: DeviceRole; isPda: boolean } {
  const { user, tenantId } = useAuth();
  const deviceId = getOrCreateDeviceId();
  const { data } = useQuery({
    queryKey: ['device_role', tenantId, deviceId],
    enabled: !!user && !!tenantId,
    placeholderData: keepPreviousData,
    staleTime: REFRESH_MS / 2,
    refetchInterval: REFRESH_MS,
    queryFn: async () => {
      const result = await getMyDeviceRole(deviceId);
      // Hors ligne ou erreur : on garde le dernier rôle connu.
      if (!result.ok) return readStored(tenantId) ?? 'standard';
      return result.value;
    },
  });

  useEffect(() => {
    if (data && tenantId) writeStored({ tenantId, role: data });
  }, [data, tenantId]);

  const role = data ?? readStored(tenantId) ?? 'standard';
  return { role, isPda: role === 'pda_stock' };
}
